// ── Impressora líder por navegador ──────────────────────────────────────────
// Centraliza a impressão: elege UMA aba "impressora" por navegador (Web Lock) e
// faz TODAS as impressões saírem só por ela. As demais abas, em vez de imprimir,
// encaminham o job para a líder via BroadcastChannel — só a líder chama de fato o
// `mandarImprimir`.
//
// Motivo: o backend transmite o mesmo evento de impressão para todas as abas e,
// com a impressão alternada, ainda vem um push por setor. Cada caminho tinha a
// sua própria trava (`comandasImpressas` × `impressoesProcessadas`), sem
// cruzamento entre elas, então a comanda saía 2x quando havia mais de uma aba
// aberta no mesmo navegador. Centralizando num único ponto, não importa por qual
// caminho o job chegou (aceite, estação ou push da alternada): só UMA aba imprime.
//
// A eleição usa o MESMO árbitro entre abas do líder do SSE (`navigator.locks`).
// Se a aba impressora fecha, o browser passa o lock para outra aba, que reassume
// automaticamente (e a marcação no título migra junto).

const LOCK_IMPRESSORA = "pedidos-impressora-lider"
const CANAL_IMPRESSAO = "pedidos-impressao"
// Prefixo no título da aba (a "marcação" na aba que imprime de fato).
const TITULO_PREFIXO = "🖨️ "

const suportaLocks = typeof navigator !== "undefined" && !!(navigator as any).locks
const suportaBC = typeof BroadcastChannel !== "undefined"

// Válvula de segurança por navegador: com isto ligado, a centralização é
// desativada e cada aba volta a imprimir localmente (comportamento antigo).
const centralizacaoDesativada = (): boolean => {
    try {
        return localStorage.getItem("desativarImpressoraCentralizada") === "1"
    } catch {
        return false
    }
}

let souLider = false
let canal: BroadcastChannel | null = null
let executor: ((configs: any) => void) | null = null
let iniciado = false

// Assinantes do estado de líder (ex.: o Cabeçalho mostra um selo "imprime nesta
// aba"). O título da aba nem sempre fica visível, então a UI também expõe isso.
const assinantes = new Set<(lider: boolean) => void>()
const notificar = () => {
    assinantes.forEach(cb => {
        try {
            cb(souLider)
        } catch {
            /* um assinante com erro não pode derrubar os demais */
        }
    })
}

const marcarAba = (ligar: boolean) => {
    if (typeof document === "undefined") return
    try {
        const tem = document.title.startsWith(TITULO_PREFIXO)
        if (ligar && !tem) document.title = TITULO_PREFIXO + document.title
        else if (!ligar && tem) document.title = document.title.slice(TITULO_PREFIXO.length)
    } catch {
        /* ignore */
    }
}

/**
 * Assina mudanças de "esta aba é a impressora do navegador". Chama de volta na
 * hora com o estado atual e a cada mudança. Retorna a função para cancelar.
 */
export function assinarImpressoraLider(cb: (lider: boolean) => void): () => void {
    assinantes.add(cb)
    try {
        cb(souLider)
    } catch {
        /* ignore */
    }
    return () => {
        assinantes.delete(cb)
    }
}

/**
 * Registra o executor real de impressão (o `mandarImprimir` "de verdade") e liga a
 * eleição da aba impressora + o canal de encaminhamento. Idempotente: chamadas
 * seguintes só atualizam o executor.
 */
export function iniciarImpressoraLider(exec: (configs: any) => void) {
    executor = exec
    if (iniciado) return
    iniciado = true

    // Sem suporte (ou desligado por flag): cada aba imprime local, como antes.
    if (!suportaLocks || !suportaBC || centralizacaoDesativada()) {
        souLider = true
        notificar()
        return
    }

    canal = new BroadcastChannel(CANAL_IMPRESSAO)
    canal.onmessage = e => {
        const msg = e.data
        if (!msg || msg.tipo !== "imprimir") return
        // Só a líder executa; as demais abas ignoram jobs encaminhados.
        if (souLider) executor?.(msg.configs)
    }

    ;(navigator as any).locks
        .request(
            LOCK_IMPRESSORA,
            { mode: "exclusive" },
            () =>
                new Promise<void>(() => {
                    // Virou a impressora do navegador; segura o lock enquanto a aba
                    // viver (a Promise nunca resolve). Ao fechar, o lock é liberado
                    // e outra aba assume.
                    souLider = true
                    marcarAba(true)
                    notificar()
                })
        )
        .catch(() => {
            /* AbortError etc.: segue como não-líder (encaminha para a líder). */
        })
}

/** True se ESTA aba é a impressora do navegador (a que imprime de fato). */
export function souImpressoraLider(): boolean {
    return souLider
}

/**
 * Roteia o job de impressão entre as abas. Retorna:
 *  - `false` → imprima NESTA aba (é a líder, ou não há centralização ativa);
 *  - `true`  → job encaminhado para a aba líder; NÃO imprima aqui.
 */
export function encaminharImpressao(configs: any): boolean {
    if (souLider || !canal) return false
    try {
        // `qz` (websocket/funcs) não é serializável e a líder o reconstrói do
        // próprio store — removemos antes de mandar pelo canal.
        const { qz, ...limpo } = configs || {}
        canal.postMessage({ tipo: "imprimir", configs: limpo })
        return true
    } catch {
        // Falha ao encaminhar (ex.: payload não serializável): imprime local como
        // fallback, para o pedido não deixar de sair.
        return false
    }
}
